/**
 * LinkedIn Integration Store
 * Manages LinkedIn connection state, posts, and analytics
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  LinkedInIntegrationConfig,
  LinkedInPost,
  LinkedInOrganization,
  LinkedInPostMetrics,
} from '../types';

interface LinkedInState {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  config: LinkedInIntegrationConfig | null;
  organizations: LinkedInOrganization[];
  selectedOrganization: LinkedInOrganization | null;

  // Posts
  posts: LinkedInPost[];
  isLoadingPosts: boolean;

  // Analytics
  analytics: {
    totalPosts: number;
    totalEngagement: number;
    avgEngagementRate: number;
    topPerformingPosts: LinkedInPost[];
  } | null;
  isLoadingAnalytics: boolean;

  // API status
  apiStatus: {
    configured: boolean;
    marketingApiEnabled: boolean;
    capabilities: {
      organizationPosting: boolean;
      personalPosting: boolean;
      metrics: {
        likes: boolean;
        comments: boolean;
        impressions: boolean;
        clicks: boolean;
        shares: boolean;
        engagementRate: boolean;
      };
    };
  } | null;

  // Error state
  error: string | null;

  // Actions
  setConnecting: (isConnecting: boolean) => void;
  setConnected: (config: LinkedInIntegrationConfig) => void;
  disconnect: () => void;
  setOrganizations: (organizations: LinkedInOrganization[]) => void;
  selectOrganization: (organization: LinkedInOrganization) => void;
  setPosts: (posts: LinkedInPost[]) => void;
  addPost: (post: LinkedInPost) => void;
  updatePost: (id: string, updates: Partial<LinkedInPost>) => void;
  removePost: (id: string) => void;
  setAnalytics: (analytics: LinkedInState['analytics']) => void;
  setApiStatus: (status: LinkedInState['apiStatus']) => void;
  setError: (error: string | null) => void;
  setLoadingPosts: (loading: boolean) => void;
  setLoadingAnalytics: (loading: boolean) => void;
  updatePostMetrics: (postId: string, metrics: LinkedInPostMetrics) => void;
}

export const useLinkedInStore = create<LinkedInState>()(
  persist(
    (set, get) => ({
      // Initial state
      isConnected: false,
      isConnecting: false,
      config: null,
      organizations: [],
      selectedOrganization: null,
      posts: [],
      isLoadingPosts: false,
      analytics: null,
      isLoadingAnalytics: false,
      apiStatus: null,
      error: null,

      // Actions
      setConnecting: (isConnecting) => set({ isConnecting, error: null }),

      setConnected: (config) =>
        set({
          isConnected: true,
          isConnecting: false,
          config,
          error: null,
        }),

      disconnect: () =>
        set({
          isConnected: false,
          isConnecting: false,
          config: null,
          organizations: [],
          selectedOrganization: null,
          posts: [],
          analytics: null,
          error: null,
        }),

      setOrganizations: (organizations) => set({ organizations }),

      selectOrganization: (organization) =>
        set((state) => ({
          selectedOrganization: organization,
          config: state.config
            ? {
                ...state.config,
                organizationId: organization.id,
                organizationName: organization.localizedName,
              }
            : null,
        })),

      setPosts: (posts) => set({ posts, isLoadingPosts: false }),

      addPost: (post) =>
        set((state) => ({
          posts: [post, ...state.posts],
        })),

      updatePost: (id, updates) =>
        set((state) => ({
          posts: state.posts.map((p) => (p.id === id ? { ...p, ...updates } : p)),
        })),

      removePost: (id) =>
        set((state) => ({
          posts: state.posts.filter((p) => p.id !== id),
        })),

      setAnalytics: (analytics) => set({ analytics, isLoadingAnalytics: false }),

      setApiStatus: (apiStatus) => set({ apiStatus }),

      setError: (error) => set({ error, isConnecting: false }),

      setLoadingPosts: (isLoadingPosts) => set({ isLoadingPosts }),

      setLoadingAnalytics: (isLoadingAnalytics) => set({ isLoadingAnalytics }),

      updatePostMetrics: (postId, metrics) =>
        set((state) => ({
          posts: state.posts.map((p) =>
            p.id === postId ? { ...p, metrics } : p
          ),
        })),
    }),
    {
      name: 'linkedin-integration',
      partialize: (state) => ({
        isConnected: state.isConnected,
        config: state.config,
        selectedOrganization: state.selectedOrganization,
      }),
    }
  )
);

// Selectors
export const selectIsConnected = (state: LinkedInState) => state.isConnected;
export const selectConfig = (state: LinkedInState) => state.config;
export const selectOrganizations = (state: LinkedInState) => state.organizations;
export const selectSelectedOrganization = (state: LinkedInState) => state.selectedOrganization;
export const selectPosts = (state: LinkedInState) => state.posts;
export const selectAnalytics = (state: LinkedInState) => state.analytics;
export const selectApiStatus = (state: LinkedInState) => state.apiStatus;
export const selectError = (state: LinkedInState) => state.error;
